import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { CommonModule } from '@angular/common';
import { CommonValidatorService } from '../../../../core/services/validators/common-validator.service';
import { MarketDataService } from '../../../../core/api/market-data.service';
import { lastValueFrom } from 'rxjs';
import { MarketDataElectricity, MarketDataGas } from '../../../../core/interfaces/market-data-interfaces';
import { ToastComponent } from "../../../../shared/components/toast/toast.component";
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { Router } from '@angular/router';
import { CustomNumberInputComponent } from "../../../../shared/components/custom-number-input/custom-number-input.component";
import { AdminNavigationComponent } from "../../components/admin-navigation/admin-navigation.component";
import { AdminTopbarComponent } from "../../components/admin-topbar/admin-topbar.component";

@Component({
  selector: 'app-admin-panel-page',
  standalone: true,
  imports: [TranslocoPipe, CustomButtonComponent, CommonModule, ToastComponent, CustomFooterComponent, CustomNumberInputComponent, AdminNavigationComponent, AdminTopbarComponent],
  templateUrl: './admin-panel-page.component.html',
  styleUrl: './admin-panel-page.component.css'
})
export class AdminPanelPageComponent implements OnInit {
  editing: boolean = false;

  // First form data
  dispatchingFactorInput: string = '';
  dispatchingFactorInputError: string | null = null;

  marketCapacityPeakInput: string = '';
  marketCapacityPeakInputError: string | null = null;

  marketCapacityLowInput: string = '';
  marketCapacityLowInputError: string | null = null;

  transportationFactor1Input: string = '';
  transportationFactor1InputError: string | null = null;

  transportationFactor2Input: string = '';
  transportationFactor2InputError: string | null = null;

  systemChargesInput: string = '';
  systemChargesInputError: string | null = null;

  taxRate1Input: string = '';
  taxRate1InputError: string | null = null;

  taxRate2Input: string = '';
  taxRate2InputError: string | null = null;

  // Second form data
  standardPCSInput: string = '';
  standardPCSInputError: string | null = null;

  gasMeterFeeInput: string = '';
  gasMeterFeeInputError: string | null = null;

  costEqualisationFeeInput: string = '';
  costEqualisationFeeInputError: string | null = null;

  variableDistributionCost1: string = '';
  variableDistributionCost1Error: string | null = null;

  variableDistributionCost2: string = '';
  variableDistributionCost2Error: string | null = null;

  variableDistributionCost3: string = '';
  variableDistributionCost3Error: string | null = null;

  variableDistributionCost4: string = '';
  variableDistributionCost4Error: string | null = null;

  variableDistributionCost5: string = '';
  variableDistributionCost5Error: string | null = null;

  variableDistributionCost6: string = '';
  variableDistributionCost6Error: string | null = null;

  variableDistributionCost7: string = '';
  variableDistributionCost7Error: string | null = null;

  variableDistributionCost8: string = '';
  variableDistributionCost8Error: string | null = null;

  stateTaxRateInput: string = '';
  stateTaxRateInputError: string | null = null;

  regionTaxRateInput: string = '';
  regionTaxRateInputError: string | null = null;

  // Market Data API
  marketDataLoaded: boolean = false;
  marketDataLoadingError: boolean = false;

  marketDataElectricity: MarketDataElectricity | null = null;
  marketDataGas: MarketDataGas | null = null;

  // Check if data is updated first time
  firstTimeUpdate: boolean = true;

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private commonValidator: CommonValidatorService, private marketData: MarketDataService, private apiError: ApiErrorService, private router: Router) { }

  ngOnInit(): void {
    this.loadMarketData();
  }

  resetErrors(): void {
    this.dispatchingFactorInputError = this.marketCapacityPeakInputError =
      this.marketCapacityLowInputError = this.transportationFactor1InputError =
      this.transportationFactor2InputError = this.systemChargesInputError =
      this.taxRate1InputError = this.taxRate2InputError =
      this.standardPCSInputError = this.gasMeterFeeInputError =
      this.costEqualisationFeeInputError = this.variableDistributionCost1Error =
      this.variableDistributionCost2Error = this.variableDistributionCost3Error =
      this.variableDistributionCost4Error = this.variableDistributionCost5Error =
      this.variableDistributionCost6Error = this.variableDistributionCost7Error =
      this.variableDistributionCost8Error = this.stateTaxRateInputError =
      this.regionTaxRateInputError = null;
  }

  validateInputs(): boolean {
    // Trim input values
    const dispatchingFactorValue = this.dispatchingFactorInput.trim();
    const marketCapacityPeakValue = this.marketCapacityPeakInput.trim();
    const marketCapacityLowValue = this.marketCapacityLowInput.trim();
    const transportationFactor1Value = this.transportationFactor1Input.trim();
    const transportationFactor2Value = this.transportationFactor2Input.trim();
    const systemChargesValue = this.systemChargesInput.trim();
    const taxRate1Value = this.taxRate1Input.trim();
    const taxRate2Value = this.taxRate2Input.trim();

    const standardPCSValue = this.standardPCSInput.trim();
    const gasMeterFeeValue = this.gasMeterFeeInput.trim();
    const costEqualisationFeeValue = this.costEqualisationFeeInput.trim();
    const variableDistributionCost1Value = this.variableDistributionCost1.trim();
    const variableDistributionCost2Value = this.variableDistributionCost2.trim();
    const variableDistributionCost3Value = this.variableDistributionCost3.trim();
    const variableDistributionCost4Value = this.variableDistributionCost4.trim();
    const variableDistributionCost5Value = this.variableDistributionCost5.trim();
    const variableDistributionCost6Value = this.variableDistributionCost6.trim();
    const variableDistributionCost7Value = this.variableDistributionCost7.trim();
    const variableDistributionCost8Value = this.variableDistributionCost8.trim();
    const stateTaxRateValue = this.stateTaxRateInput.trim();
    const regionTaxRateValue = this.regionTaxRateInput.trim();

    // Validate inputs
    this.dispatchingFactorInputError = this.commonValidator.validateNumber(dispatchingFactorValue);
    this.marketCapacityPeakInputError = this.commonValidator.validateNumber(marketCapacityPeakValue);
    this.marketCapacityLowInputError = this.commonValidator.validateNumber(marketCapacityLowValue);
    this.transportationFactor1InputError = this.commonValidator.validateNumber(transportationFactor1Value);
    this.transportationFactor2InputError = this.commonValidator.validateNumber(transportationFactor2Value);
    this.systemChargesInputError = this.commonValidator.validateNumber(systemChargesValue);
    this.taxRate1InputError = this.commonValidator.validateNumber(taxRate1Value);
    this.taxRate2InputError = this.commonValidator.validateNumber(taxRate2Value);

    this.standardPCSInputError = this.commonValidator.validateNumber(standardPCSValue);
    this.gasMeterFeeInputError = this.commonValidator.validateNumber(gasMeterFeeValue);
    this.costEqualisationFeeInputError = this.commonValidator.validateNumber(costEqualisationFeeValue);
    this.variableDistributionCost1Error = this.commonValidator.validateNumber(variableDistributionCost1Value);
    this.variableDistributionCost2Error = this.commonValidator.validateNumber(variableDistributionCost2Value);
    this.variableDistributionCost3Error = this.commonValidator.validateNumber(variableDistributionCost3Value);
    this.variableDistributionCost4Error = this.commonValidator.validateNumber(variableDistributionCost4Value);
    this.variableDistributionCost5Error = this.commonValidator.validateNumber(variableDistributionCost5Value);
    this.variableDistributionCost6Error = this.commonValidator.validateNumber(variableDistributionCost6Value);
    this.variableDistributionCost7Error = this.commonValidator.validateNumber(variableDistributionCost7Value);
    this.variableDistributionCost8Error = this.commonValidator.validateNumber(variableDistributionCost8Value);
    this.stateTaxRateInputError = this.commonValidator.validateNumber(stateTaxRateValue);
    this.regionTaxRateInputError = this.commonValidator.validateNumber(regionTaxRateValue);


    // Check if any errors exist
    if (
      this.dispatchingFactorInputError || this.marketCapacityPeakInputError ||
      this.marketCapacityLowInputError || this.transportationFactor1InputError ||
      this.transportationFactor2InputError || this.systemChargesInputError ||
      this.taxRate1InputError || this.taxRate2InputError ||
      this.standardPCSInputError || this.gasMeterFeeInputError ||
      this.costEqualisationFeeInputError ||
      this.variableDistributionCost1Error || this.variableDistributionCost2Error ||
      this.variableDistributionCost3Error || this.variableDistributionCost4Error ||
      this.variableDistributionCost5Error || this.variableDistributionCost6Error ||
      this.variableDistributionCost7Error || this.variableDistributionCost8Error ||
      this.stateTaxRateInputError || this.regionTaxRateInputError
    ) {
      return false;
    }

    return true;
  }

  populateElectricityMarketData(marketData: MarketDataElectricity) {
    if (!marketData) {
      return;
    }

    this.dispatchingFactorInput = marketData.dispatching_factor?.toString() ?? '';
    this.marketCapacityPeakInput = marketData.market_capacity_peak_hours?.toString() ?? '';
    this.marketCapacityLowInput = marketData.market_capacity_low_hours?.toString() ?? '';
    this.transportationFactor1Input = marketData.trans_meas_factor_1?.toString() ?? '';
    this.transportationFactor2Input = marketData.trans_meas_factor_2?.toString() ?? '';
    this.systemChargesInput = marketData.system_charges_factor?.toString() ?? '';
    this.taxRate1Input = marketData.tax_rate_1?.toString() ?? '';
    this.taxRate2Input = marketData.tax_rate_2?.toString() ?? '';
  }

  getElectricityMarketDataFromInputs(id: string = ''): MarketDataElectricity {
    return {
      id: id,
      dispatching_factor: parseFloat(this.dispatchingFactorInput),
      market_capacity_peak_hours: parseFloat(this.marketCapacityPeakInput),
      market_capacity_low_hours: parseFloat(this.marketCapacityLowInput),
      trans_meas_factor_1: parseFloat(this.transportationFactor1Input),
      trans_meas_factor_2: parseFloat(this.transportationFactor2Input),
      system_charges_factor: parseFloat(this.systemChargesInput),
      tax_rate_1: parseFloat(this.taxRate1Input),
      tax_rate_2: parseFloat(this.taxRate2Input)
    };
  }

  populateGasMarketData(marketData: MarketDataGas) {
    if (!marketData) {
      return;
    }

    this.standardPCSInput = marketData.standard_pcs?.toString() ?? '';
    this.gasMeterFeeInput = marketData.gas_meter_fees?.toString() ?? '';
    this.costEqualisationFeeInput = marketData.cost_equalisation_fees?.toString() ?? '';
    this.variableDistributionCost1 = marketData.var_dist_cost_1?.toString() ?? '';
    this.variableDistributionCost2 = marketData.var_dist_cost_2?.toString() ?? '';
    this.variableDistributionCost3 = marketData.var_dist_cost_3?.toString() ?? '';
    this.variableDistributionCost4 = marketData.var_dist_cost_4?.toString() ?? '';
    this.variableDistributionCost5 = marketData.var_dist_cost_5?.toString() ?? '';
    this.variableDistributionCost6 = marketData.var_dist_cost_6?.toString() ?? '';
    this.variableDistributionCost7 = marketData.var_dist_cost_7?.toString() ?? '';
    this.variableDistributionCost8 = marketData.var_dist_cost_8?.toString() ?? '';
    this.stateTaxRateInput = marketData.state_tax_rate?.toString() ?? '';
    this.regionTaxRateInput = marketData.region_tax_rate?.toString() ?? '';
  }

  getGasMarketDataFromInputs(id: string = ''): MarketDataGas {
    return {
      id: id,
      standard_pcs: parseFloat(this.standardPCSInput),
      gas_meter_fees: parseFloat(this.gasMeterFeeInput),
      cost_equalisation_fees: parseFloat(this.costEqualisationFeeInput),
      var_dist_cost_1: parseFloat(this.variableDistributionCost1),
      var_dist_cost_2: parseFloat(this.variableDistributionCost2),
      var_dist_cost_3: parseFloat(this.variableDistributionCost3),
      var_dist_cost_4: parseFloat(this.variableDistributionCost4),
      var_dist_cost_5: parseFloat(this.variableDistributionCost5),
      var_dist_cost_6: parseFloat(this.variableDistributionCost6),
      var_dist_cost_7: parseFloat(this.variableDistributionCost7),
      var_dist_cost_8: parseFloat(this.variableDistributionCost8),
      state_tax_rate: parseFloat(this.stateTaxRateInput),
      region_tax_rate: parseFloat(this.regionTaxRateInput)
    };
  }

  async loadMarketData() {
    this.marketDataLoaded = false;
    this.marketDataLoadingError = false;

    this.marketDataGas = null;
    this.marketDataElectricity = null;

    // Load electricity market data
    try {
      this.marketDataElectricity = await lastValueFrom(this.marketData.getElectricityMarketData());
      this.populateElectricityMarketData(this.marketDataElectricity!);
    } catch (error) {
      this.marketDataLoadingError = true;
      var showError = true;

      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        if (error.error.code === 'SVC-4004') {
          showError = false;
          this.marketDataLoadingError = false;
        }
      }

      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
    }

    // Load gas market data
    try {
      this.marketDataGas = await lastValueFrom(this.marketData.getGasMarketData());
      this.populateGasMarketData(this.marketDataGas!);
    } catch (error) {
      var showError = true;

      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        if (error.error.code === 'SVC-4004') {
          showError = false;
        }
      }

      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        this.marketDataLoadingError = true;
      }
    }

    this.marketDataLoaded = true;
  }

  editButtonClick() {
    this.editing = true;
  }

  cancelButtonClick() {
    this.editing = false;
    this.populateElectricityMarketData(this.marketDataElectricity!);
    this.populateGasMarketData(this.marketDataGas!);
    this.toast.showToast('admin.canceled', 'admin.data_restored', 'warning', 4000, true);
  }

  async firstTimeCreateData(): Promise<boolean> {
    try {
      this.marketDataElectricity = await lastValueFrom(this.marketData.createElectricityMarketData(this.getElectricityMarketDataFromInputs()));
      this.marketDataGas = await lastValueFrom(this.marketData.createGasMarketData(this.getGasMarketDataFromInputs()));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return false;
    }
    return true;
  }

  async updateData(): Promise<boolean> {
    try {
      this.marketDataElectricity = await lastValueFrom(this.marketData.editElectricityMarketData(this.getElectricityMarketDataFromInputs(this.marketDataElectricity!.id)));
      this.marketDataGas = await lastValueFrom(this.marketData.editGasMarketData(this.getGasMarketDataFromInputs(this.marketDataGas!.id)));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return false;
    }
    return true;
  }


  async saveButtonClick() {
    if (!this.validateInputs()) {
      return;
    }

    var responseStatus = (this.firstTimeUpdate) ? await this.firstTimeCreateData() : await this.updateData();
    if (!responseStatus) {
      return;
    }

    this.populateElectricityMarketData(this.marketDataElectricity!);
    this.populateGasMarketData(this.marketDataGas!);

    this.firstTimeUpdate = false;
    this.toast.showToast('shared.success', 'shared.toast.data_updated', 'success', 4000, true);
    this.editing = false;
  }

  goToDashboard() {
    this.router.navigate(["/dashboard"]);
  }
}
